// src/pages/Admin/Analytics/AnalyticsDashboard.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Eye,
  Clock,
  Heart,
  Download,
  BarChart3,
  PieChart,
  Activity,
  Radio,
  MessageSquare
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { useToast } from '../../../hooks/useToast';
import adminService from '../../../services/adminService';
import liveStreamService, { type LiveStreamAnalytics } from '../../../services/liveStreamService';

interface AnalyticsData {
  overview: {
    total_views: number;
    total_users: number;
    total_content: number;
    engagement_rate: number;
    avg_session_duration: number;
    /** Absent when there is no session tracking to derive it from. */
    bounce_rate: number | null;
  };
  content_performance: Array<{
    id: string;
    title: string;
    content_type: string;
    views: number;
    likes: number;
    engagement_rate: number;
    trending: boolean;
  }>;
  user_engagement: Array<{
    date: string;
    daily_active_users: number;
    new_users: number;
    returning_users: number;
  }>;
  content_distribution: Array<{
    content_type: string;
    count: number;
    percentage: number;
  }>;
  geographic_data: Array<{
    country: string;
    user_count: number;
    percentage: number;
  }>;
}

const AnalyticsDashboard: React.FC = () => {
  const { showError } = useToast();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [liveStreamAnalytics, setLiveStreamAnalytics] = useState<LiveStreamAnalytics | null>(null);
  const [dateRange, setDateRange] = useState('7d');
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const isRequestInProgressRef = useRef(false);
  const showErrorRef = useRef(showError);

  const dateRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 3 months' },
    { value: '1y', label: 'Last year' }
  ];

  // Keep refs in sync with state
  useEffect(() => {
    isRequestInProgressRef.current = isRequestInProgress;
  }, [isRequestInProgress]);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  // Auto-scroll based on route
  useEffect(() => {
    if (!loading && analyticsData) {
      const path = location.pathname;
      let targetSection = '';
      
      if (path.includes('/analytics/users')) {
        targetSection = 'user-engagement';
        setSelectedCard('users');
      } else if (path.includes('/analytics/content')) {
        targetSection = 'content-performance';
        setSelectedCard('views');
      } else if (path.includes('/analytics/engagement')) {
        targetSection = 'user-engagement';
        setSelectedCard('engagement');
      }
      
      if (targetSection) {
        setTimeout(() => {
          const targetElement = document.getElementById(targetSection);
          if (targetElement) {
            targetElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
            
            // Clear selection after highlighting
            setTimeout(() => setSelectedCard(null), 2000);
          }
        }, 500); // Give time for data to render
      }
    }
  }, [location.pathname, loading, analyticsData]);

  const loadAnalyticsData = useCallback(async () => {
    // Prevent multiple simultaneous requests using ref to avoid dependency
    if (isRequestInProgressRef.current) {
      return;
    }
    
    try {
      setIsRequestInProgress(true);
      isRequestInProgressRef.current = true;
      setLoading(true);
      


      try {
        // Fetch real analytics data from backend
        const [data, streamAnalytics] = await Promise.all([
          adminService.getAnalyticsDashboard(dateRange),
          liveStreamService.getLiveStreamAnalytics()
        ]);
        
        if (isMountedRef.current) {
          setAnalyticsData(data);
          setLiveStreamAnalytics(streamAnalytics);
        }
      } catch (_error) {
        if (isMountedRef.current) {
          showErrorRef.current('Failed to load analytics data');
        }
      }
      
    } catch {
      if (isMountedRef.current) {
        showErrorRef.current('Failed to load analytics data');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setIsRequestInProgress(false);
        isRequestInProgressRef.current = false;
      }
    }
  }, [dateRange]); // Stable dependencies to prevent infinite re-renders

  useEffect(() => {
    isMountedRef.current = true;
    // Only load data on mount or when dateRange changes, not when loading states change
    loadAnalyticsData();
    
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]); // Only depend on dateRange to prevent infinite loops caused by loadAnalyticsData dependencies

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Handle card clicks to scroll to relevant sections or show more details
  const handleCardClick = (cardType: string) => {
    setSelectedCard(cardType);
    
    let targetId = '';
    switch (cardType) {
      case 'views':
        targetId = 'content-performance';
        break;
      case 'users':
        targetId = 'user-engagement';
        break;
      case 'content':
        targetId = 'content-distribution';
        break;
      case 'engagement':
      case 'session':
      case 'bounce':
        targetId = 'user-engagement';
        break;
      default:
        return;
    }
    
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      // Add a visual indicator immediately
      targetElement.style.border = '2px solid #8b5cf6';
      targetElement.style.borderRadius = '8px';
      
      // Try multiple scroll methods to ensure it works
      try {
        // Method 1: Standard scrollIntoView
        targetElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
        
        // Clear the visual indicator after animation
        setTimeout(() => {
          targetElement.style.border = '';
          targetElement.style.borderRadius = '';
        }, 2000);
        
      } catch {
        // Fallback: instant scroll
        targetElement.scrollIntoView({ block: 'center' });
        
        // Clear the visual indicator
        setTimeout(() => {
          targetElement.style.border = '';
          targetElement.style.borderRadius = '';
        }, 2000);
      }
    }
    
    // Clear selection after a delay
    setTimeout(() => setSelectedCard(null), 2000);
  };

  if (loading || !analyticsData || isRequestInProgress) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mb-4"></div>
        <p className="text-gray-600">Loading analytics data...</p>
      </div>
    );
  }

  // Get current view title based on route
  const getCurrentViewTitle = () => {
    const path = location.pathname;
    if (path.includes('/analytics/users')) {
      return 'User Analytics';
    } else if (path.includes('/analytics/content')) {
      return 'Content Analytics';
    } else if (path.includes('/analytics/engagement')) {
      return 'Engagement Metrics';
    }
    return 'Analytics Dashboard';
  };

  const getCurrentViewDescription = () => {
    const path = location.pathname;
    if (path.includes('/analytics/users')) {
      return 'User behavior, growth, and engagement trends';
    } else if (path.includes('/analytics/content')) {
      return 'Content performance, distribution, and popularity insights';
    } else if (path.includes('/analytics/engagement')) {
      return 'User engagement rates, session data, and interaction metrics';
    }
    return 'Platform performance and user engagement insights';
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{getCurrentViewTitle()}</h1>
            <p className="mt-1 text-sm sm:text-base text-gray-600">
              {getCurrentViewDescription()}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              {dateRangeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            
            <button className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg flex items-center justify-center transition-colors">
              <Download className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Export Report</span>
              <span className="sm:hidden">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
        <div 
          className={`bg-white rounded-lg shadow p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
            selectedCard === 'views' ? 'ring-2 ring-blue-500 shadow-lg' : ''
          }`}
          onClick={() => handleCardClick('views')}
          title="Click to view content performance details"
        >
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Eye className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Views</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatNumber(analyticsData.overview.total_views)}
              </p>
            </div>
          </div>
        </div>

        <div 
          className={`bg-white rounded-lg shadow p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
            selectedCard === 'users' ? 'ring-2 ring-orange-500 shadow-lg' : ''
          }`}
          onClick={() => handleCardClick('users')}
          title="Click to view user engagement details"
        >
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatNumber(analyticsData.overview.total_users)}
              </p>
            </div>
          </div>
        </div>

        <div 
          className={`bg-white rounded-lg shadow p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
            selectedCard === 'content' ? 'ring-2 ring-green-500 shadow-lg' : ''
          }`}
          onClick={() => handleCardClick('content')}
          title="Click to view content distribution details"
        >
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Content</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatNumber(analyticsData.overview.total_content)}
              </p>
            </div>
          </div>
        </div>

        <div 
          className={`bg-white rounded-lg shadow p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
            selectedCard === 'engagement' ? 'ring-2 ring-orange-500 shadow-lg' : ''
          }`}
          onClick={() => handleCardClick('engagement')}
          title="Click to view engagement details"
        >
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Activity className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Engagement Rate</p>
              <p className="text-2xl font-semibold text-gray-900">
                {analyticsData.overview.engagement_rate}%
              </p>
            </div>
          </div>
        </div>

        <div 
          className={`bg-white rounded-lg shadow p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
            selectedCard === 'session' ? 'ring-2 ring-orange-500 shadow-lg' : ''
          }`}
          onClick={() => handleCardClick('session')}
          title="Click to view session details"
        >
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Session</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatDuration(analyticsData.overview.avg_session_duration)}
              </p>
            </div>
          </div>
        </div>

        <div 
          className={`bg-white rounded-lg shadow p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
            selectedCard === 'bounce' ? 'ring-2 ring-red-500 shadow-lg' : ''
          }`}
          onClick={() => handleCardClick('bounce')}
          title="Click to view bounce rate details"
        >
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrendingDown className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Bounce Rate</p>
              {/* This tile read 23.4% for every date range, forever - the backend returned
                  that constant. Working it out needs session tracking, which does not
                  exist, so the tile says so instead of printing a number. */}
              {analyticsData.overview.bounce_rate === null ||
               analyticsData.overview.bounce_rate === undefined ? (
                <>
                  <p className="text-lg font-semibold text-gray-500">Not measured</p>
                  <p className="text-xs text-gray-500">Needs session tracking</p>
                </>
              ) : (
                <p className="text-2xl font-semibold text-gray-900">
                  {analyticsData.overview.bounce_rate}%
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        {/* Content Performance */}
        <div id="content-performance" className={`bg-white rounded-lg shadow p-6 transition-all duration-300 ${
          selectedCard === 'views' ? 'ring-2 ring-blue-500 shadow-xl' : ''
        }`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Top Performing Content</h3>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          
          {/* Bar Chart */}
          <div className="h-64 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analyticsData.content_performance.slice(0, 5)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="title" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    formatNumber(value),
                    name === 'views' ? 'Views' : name === 'likes' ? 'Likes' : 'Engagement Rate'
                  ]}
                />
                <Legend />
                <Bar dataKey="views" fill="#8b5cf6" name="Views" />
                <Bar dataKey="likes" fill="#10b981" name="Likes" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Content List */}
          <div className="space-y-3">
            {analyticsData.content_performance.map((content, index) => (
              <div key={content.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="text-sm font-medium text-gray-500 w-6">
                    #{index + 1}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {content.title}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {content.content_type} • {content.engagement_rate}% engagement
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="flex items-center text-gray-600">
                      <Eye className="h-4 w-4 mr-1" />
                      {formatNumber(content.views)}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Heart className="h-4 w-4 mr-1" />
                      {formatNumber(content.likes)}
                    </div>
                    {content.trending && (
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Content Distribution */}
        <div id="content-distribution" className={`bg-white rounded-lg shadow p-6 transition-all duration-300 ${
          selectedCard === 'content' ? 'ring-2 ring-green-500 shadow-xl' : ''
        }`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Content Distribution</h3>
            <PieChart className="h-5 w-5 text-gray-400" />
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-6">
            {/* Pie Chart */}
            <div className="flex-1 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={analyticsData.content_distribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="count"
                    nameKey="content_type"
                  >
                    {analyticsData.content_distribution.map((_, index) => {
                      const colors = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
                      return (
                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                      );
                    })}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number, name: string) => [
                      `${formatNumber(value)} (${analyticsData.content_distribution.find(item => item.content_type === name)?.percentage}%)`,
                      name
                    ]}
                  />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Legend/Summary */}
            <div className="flex-1 space-y-3">
              {analyticsData.content_distribution.map((item, index) => {
                const colors = [
                  'bg-orange-500',
                  'bg-blue-500',
                  'bg-green-500',
                  'bg-orange-500',
                  'bg-red-500'
                ];
                
                return (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${colors[index % colors.length]}`}></div>
                      <span className="text-sm font-medium text-gray-900">{item.content_type}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-600">{formatNumber(item.count)}</span>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">
                        {item.percentage}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Live Stream Analytics */}
      {liveStreamAnalytics && (
        <div id="live-stream-analytics" className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Radio className="h-5 w-5 text-red-500" />
              Live Stream Analytics
            </h3>
            <div className="text-sm text-gray-600">Real-time streaming insights</div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-600 text-sm mb-2">
                <Radio className="h-4 w-4" />
                Total Streams
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {formatNumber(liveStreamAnalytics.totalStreams)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {liveStreamAnalytics.currentlyLive > 0 && (
                  <span className="text-red-600 font-medium">
                    🔴 {liveStreamAnalytics.currentlyLive} live now
                  </span>
                )}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-600 text-sm mb-2">
                <Eye className="h-4 w-4" />
                Total Views
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {formatNumber(liveStreamAnalytics.totalStreamViews)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Across all streams
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
              <div className="flex items-center gap-2 text-pink-600 text-sm mb-2">
                <Heart className="h-4 w-4" />
                Total Likes
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {formatNumber(liveStreamAnalytics.totalStreamLikes)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Stream reactions
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 text-sm mb-2">
                <MessageSquare className="h-4 w-4" />
                Total Comments
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {formatNumber(liveStreamAnalytics.totalStreamComments)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Chat messages
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
              <div className="flex items-center gap-2 text-orange-600 text-sm mb-2">
                <Users className="h-4 w-4" />
                Peak Viewers
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {formatNumber(liveStreamAnalytics.peakViewersRecord)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Record concurrent viewers
              </div>
            </div>
          </div>
          
          {/* Additional Stats Row */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-gray-500">Past Streams:</span>
                <span className="ml-2 font-medium text-gray-900">{liveStreamAnalytics.totalPastStreams}</span>
              </div>
              <div>
                <span className="text-gray-500">With Recordings:</span>
                <span className="ml-2 font-medium text-gray-900">{liveStreamAnalytics.streamsWithRecordings}</span>
              </div>
              {liveStreamAnalytics.totalLiveViewers > 0 && (
                <div>
                  <span className="text-gray-500">Live Viewers Now:</span>
                  <span className="ml-2 font-medium text-red-600">{formatNumber(liveStreamAnalytics.totalLiveViewers)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Geographic Distribution */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Where members say they are</h3>
          {/* Not "Geographic Distribution". This is the country people entered on their own
              profile, which is a different claim from where they watched from - and the
              chart used to show an invented mix (United States 40.6%, United Kingdom
              14.9%) applied to the user count, with no location data behind it at all. */}
          <div className="text-sm text-gray-600">Stated on profile</div>
        </div>

        {analyticsData.geographic_data.length === 0 ? (
          <p className="text-sm text-gray-600">
            Nobody has given a country on their profile yet. Where people watched from is a
            different question and is not tracked.
          </p>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {analyticsData.geographic_data.map((country, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl font-bold text-orange-600 mb-1">
                {formatNumber(country.user_count)}
              </div>
              <div className="text-sm font-medium text-gray-900 mb-1">
                {country.country}
              </div>
              <div className="text-xs text-gray-600">
                {country.percentage}% of users
              </div>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${country.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* User Engagement Trend Chart */}
      <div id="user-engagement" className={`bg-white rounded-lg shadow p-6 transition-all duration-300 ${
        selectedCard === 'users' || selectedCard === 'engagement' || selectedCard === 'session' || selectedCard === 'bounce' ? 'ring-2 ring-orange-500 shadow-xl' : ''
      }`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">User Engagement Trend</h3>
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
              <span className="text-gray-600">Daily Active Users</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
              <span className="text-gray-600">New Users</span>
            </div>
          </div>
        </div>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={analyticsData.user_engagement}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric' 
                })}
                formatter={(value: number, name: string) => [
                  formatNumber(value),
                  name === 'daily_active_users' ? 'Daily Active Users' :
                  name === 'new_users' ? 'New Users' : 'Returning Users'
                ]}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="daily_active_users" 
                stroke="#8b5cf6" 
                strokeWidth={2}
                name="Daily Active Users"
              />
              <Line 
                type="monotone" 
                dataKey="new_users" 
                stroke="#10b981" 
                strokeWidth={2}
                name="New Users"
              />
              <Line 
                type="monotone" 
                dataKey="returning_users" 
                stroke="#3b82f6" 
                strokeWidth={2}
                name="Returning Users"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Summary table below chart */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-4 gap-4 text-sm font-medium text-gray-600 mb-2">
            <div>Date</div>
            <div>Active Users</div>
            <div>New Users</div>
            <div>Returning Users</div>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {analyticsData.user_engagement.map((day, index) => (
              <div key={index} className="grid grid-cols-4 gap-4 py-2 text-sm border-b border-gray-100 last:border-b-0">
                <div className="font-medium text-gray-900">
                  {new Date(day.date).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </div>
                <div className="text-orange-600">
                  {formatNumber(day.daily_active_users)}
                </div>
                <div className="text-green-600">
                  {formatNumber(day.new_users)}
                </div>
                <div className="text-blue-600">
                  {formatNumber(day.returning_users)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;